/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import {
  CommandBar,
  ICommandBarItemProps,
  Label,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import { spinnerStyles } from "Components/Common/SharedListStyles.styles";
import {
  bodyContainerStackStyles,
  commandBarStyles,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { RegionalDirectorProgramProfileEditForm } from "Components/RegionalDirectorProgramProfileEditForm/RegionalDirectorProgramProfileEditForm";
import { OperationStatus } from "Constants/OperationStatus";
import { RolesEnum } from "Constants/RolesPermissionsConstants";
import {
  getProgramProfileErrorMessage,
  getProgramProfileOperationStatus,
} from "Dataflows/MVP/ProgramProfile/IMVPProgramProfileSelector";
import { fetchProgramProfile } from "Dataflows/RD/UserProfile/RDUserProfileAction";
import { getProgramProfile } from "Dataflows/RD/UserProfile/RDUserProfileSelector";
import { formatDate } from "Utility/FormatHelper";

import { IRegionalDirectorProgramProfileProps } from "./IRegionalDirectorProgramProfileProps";
import {
  bodyColumnStackItemStyles,
  bodyContainerStackTokens,
  bodyRowStackStyles,
  labelStyles,
} from "./RegionalDirectorProgramProfile.styles";

const RegionalDirectorProgramProfile = (
  props: IRegionalDirectorProgramProfileProps
): ReactElement => {
  const { userProfileId, userProfileAwardPeriodEndDate } = props;

  const { isUserLoggedIn, loading, hasAnyRoles } = useAuth();
  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  const dispatch = useDispatch();
  const programProfile = useSelector(getProgramProfile);
  const programProfileOperationStatus = useSelector(getProgramProfileOperationStatus);
  const programProfileErrorMessage = useSelector(getProgramProfileErrorMessage);

  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    if (!!userProfileId) {
      dispatch(fetchProgramProfile(userProfileId));
    }
  }, []);

  const editProgramInformationClicked = (): void => {
    setIsPanelOpen(true);
  };

  const formatDateOrUndefined = (date: Date | undefined): string => {
    if (!!date) {
      return formatDate(date);
    } else {
      return "";
    }
  };

  const { hasRole } = useAuth();
  const canEditProgramProfile =
    hasRole(RolesEnum.RD_REVIEWER) || hasRole(RolesEnum.RD_PROGRAM_OWNER);

  if (programProfileOperationStatus?.read === OperationStatus.Pending) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (
    programProfileOperationStatus?.read === OperationStatus.Failed &&
    programProfileErrorMessage
  ) {
    return <div>{programProfileErrorMessage}</div>;
  }

  const items: ICommandBarItemProps[] = [
    {
      key: "editProgramInformation",
      text: "Edit Program Details",
      iconProps: { iconName: "Edit" },
      onClick: (): void => {
        editProgramInformationClicked();
      },
    },
  ];

  return (
    <>
      {!programProfile ? (
        <Spinner size={SpinnerSize.large} styles={spinnerStyles} />
      ) : (
        <>
          {canEditProgramProfile && (
            <CommandBar items={items} ariaLabel="Edit Program Details" styles={commandBarStyles} />
          )}
          <ContainerWrapper>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>Program Details</Text>
            </Stack>
            <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Application Status</Label>
                  <Text>{programProfile?.applicationStatus}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Date Last Modified</Label>
                  <Text>{formatDateOrUndefined(programProfile?.dateLastModified)}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Date Submitted</Label>
                  <Text>{formatDateOrUndefined(programProfile?.dateSubmitted)}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Program Profile Status</Label>
                  <Text>{programProfile?.programProfileStatus}</Text>
                </StackItem>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Program End Date</Label>
                  <Text>{formatDateOrUndefined(userProfileAwardPeriodEndDate)}</Text>
                </StackItem>
              </Stack>
              <Stack horizontal styles={bodyRowStackStyles}>
                <StackItem styles={bodyColumnStackItemStyles}>
                  <Label styles={labelStyles}>Contributor ID</Label>
                  <Text>{programProfile?.microsoftReferralId}</Text>
                </StackItem>
              </Stack>
            </Stack>
          </ContainerWrapper>
        </>
      )}
      {isPanelOpen && (
        <RegionalDirectorProgramProfileEditForm
          isPanelOpen={isPanelOpen}
          setIsPanelOpen={setIsPanelOpen}
          defaultValues={{
            userProfileId,
            programProfileStatus: programProfile?.programProfileStatus,
          }}
        />
      )}
    </>
  );
};

export default RegionalDirectorProgramProfile;
